import * as Sentry from '@sentry/react'

// SDK init is a no-op when VITE_SENTRY_DSN is empty/unset, so this file is
// safe to ship before the DSN is provisioned.

const SENTRY_DSN = (import.meta.env.VITE_SENTRY_DSN ?? '').trim()
const APP_ENV = (import.meta.env.VITE_ENV ?? import.meta.env.MODE ?? 'development') as
  'development' | 'staging' | 'production'
const RELEASE = (import.meta.env.VITE_SENTRY_RELEASE as string | undefined) || undefined
const isSentryEnabled = Boolean(SENTRY_DSN)

const SCRUB_KEY_RE = /token|secret|password|api[_-]?key/i

// Phase 2 per-environment sampling table. `profiles` drives Browser Profiling
// (only collects when the page is served with `Document-Policy: js-profiling`).
const SAMPLE = (
  APP_ENV === 'production' ? { traces: 0.2, session: 0.1, error: 1.0, profiles: 0.1, debug: false } :
  APP_ENV === 'staging'    ? { traces: 1.0, session: 0.5, error: 1.0, profiles: 0.5, debug: false } :
                             { traces: 1.0, session: 0.0, error: 1.0, profiles: 1.0, debug: true }
)

// Distributed tracing — limit propagation to admin-proxy and any
// supabase.co host (admin DB direct calls). Add more brand-owned API
// domains here as they appear.
const PROPAGATE_TARGETS: (string | RegExp)[] = [
  /\.supabase\.co/,
  /^\/api\//,
]

export function initializeSentry(): void {
  if (!isSentryEnabled) {
    if (import.meta.env.DEV) console.log('[Sentry] disabled — no VITE_SENTRY_DSN configured')
    return
  }

  try {
    Sentry.init({
      dsn: SENTRY_DSN,
      environment: APP_ENV,
      release: RELEASE,
      debug: SAMPLE.debug,
      tracesSampleRate: SAMPLE.traces,
      // Browser Profiling. Only collects when the page is served with the
      // `Document-Policy: js-profiling` response header (see CF Pages _headers);
      // without it the SDK degrades silently — no errors, just no profiles.
      profilesSampleRate: SAMPLE.profiles,
      replaysSessionSampleRate: SAMPLE.session,
      replaysOnErrorSampleRate: SAMPLE.error,
      tracePropagationTargets: PROPAGATE_TARGETS,
      enabled: !import.meta.env.DEV || import.meta.env.VITE_ENABLE_SENTRY_IN_DEV === 'true',
      attachStacktrace: true,
      sendDefaultPii: false,
      integrations: [
        Sentry.browserTracingIntegration(),
        // Browser Profiling — pairs with profilesSampleRate above. Requires the
        // `Document-Policy: js-profiling` response header to actually collect;
        // degrades silently (no profiles, no errors) when the header is absent.
        Sentry.browserProfilingIntegration(),
        // Session Replay with privacy-by-default. Selective unmasking can be
        // added later via `mask`/`unmask` classNames on safe UI elements.
        Sentry.replayIntegration({
          maskAllText: true,
          maskAllInputs: true,
          blockAllMedia: true,
        }),
        // No feedbackIntegration — the auto-injected "Report a Bug" button was
        // removed on 2026-09-08. Re-adding it means re-adding the integration
        // here (use `autoInject: false` + a manual trigger if it comes back).
      ],
      ignoreErrors: [
        /ResizeObserver loop/,
        /AbortError/,
        /network request failed/i,
        // supabase-js auth lock contention between tabs — benign, auth
        // retries; fires as "Acquiring an exclusive Navigator LockManager
        // lock ... timed out waiting 10000ms"
        /Navigator LockManager/,
        /Lock broken by another request/,
      ],
      beforeBreadcrumb(breadcrumb) {
        if (breadcrumb.level === 'debug') return null
        if (breadcrumb.category?.startsWith('ui.click')) return null
        return breadcrumb
      },
      beforeSend(event) {
        if (event.request?.headers) {
          for (const k of Object.keys(event.request.headers)) {
            if (SCRUB_KEY_RE.test(k)) (event.request.headers as Record<string, unknown>)[k] = '[Filtered]'
          }
        }
        if (event.breadcrumbs) {
          event.breadcrumbs = event.breadcrumbs.map(b => {
            if (b.data) {
              for (const k of Object.keys(b.data)) {
                if (SCRUB_KEY_RE.test(k)) (b.data as Record<string, unknown>)[k] = '[Filtered]'
              }
            }
            return b
          })
        }
        return event
      },
    })
    Sentry.setTag('app', 'bmc-admin')
    Sentry.setTag('app_env', APP_ENV)
    if (import.meta.env.DEV) console.log('[Sentry] initialized')
  } catch (err) {
    console.error('[Sentry] init failed:', err)
  }
}

export function captureException(error: unknown, context?: Record<string, unknown>): void {
  if (!isSentryEnabled) return
  try {
    if (context) {
      Sentry.withScope(scope => {
        scope.setContext('custom', context)
        Sentry.captureException(error)
      })
    } else {
      Sentry.captureException(error)
    }
  } catch (e) {
    console.error('[Sentry] captureException failed:', e)
  }
}

// Phase 3 — user context wiring. Call from AuthContext on session restore /
// sign-in / sign-out. Sentry treats `null` as "no user", so we use that on
// logout rather than calling clearScope (which would also drop tags).
export function setUser(user: { id: string; email?: string } | null): void {
  if (!isSentryEnabled) return
  try {
    Sentry.setUser(user ? { id: user.id, email: user.email } : null)
  } catch (e) {
    console.error('[Sentry] setUser failed:', e)
  }
}

export function isSentryConfigured(): boolean {
  return isSentryEnabled
}

export { Sentry }
